import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { collection, query, where, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import "./Sidebar.css";

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          setLoading(true);
          const adminUsersRef = collection(db, "adminUsers");
          const q = query(adminUsersRef, where("email", "==", user.email));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const userData = userDoc.data();
            const role = userData.role;
            setUserRole(role);
            console.log("User role found:", role);
          } else {
            console.log("No user document found for:", user.email);
            setError("User role not found");
          }
        } catch (error) {
          console.error("Error fetching user role: ", error.message);
          setError("Failed to load user data");
        } finally {
          setLoading(false);
        }
      } else {
        setUserRole(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log("User signed out");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error signing out: ", error.message);
      setError("Failed to logout");
    }
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const isActive = (path) => {
    return location.pathname === path ? "active" : "";
  };

  const menuItems = [
    {
      path: "/admin",
      icon: "fas fa-tachometer-alt",
      label: "Dashboard",
      roles: [0, 1],
    },
    {
      path: "/manage-users",
      icon: "fas fa-users",
      label: "Manage Users",
      roles: [0, 1],
    },
    {
      path: "/subscription-plan",
      icon: "fas fa-crown",
      label: "Subscription Plans",
      roles: [0, 1],
    },
    {
      path: "/dailylimit-swipecount",
      icon: "fas fa-chart-line",
      label: "Daily Limit Swipes Count",
      roles: [0, 1],
    },
    {
      path: "/privacy-policy",
      icon: "fas fa-shield-alt",
      label: "Privacy Policy",
      roles: [0, 1],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles.includes(userRole)
  );

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button className="mobile-menu-toggle" onClick={toggleMobileSidebar}>
        <i className={`fas ${isMobileOpen ? "fa-times" : "fa-bars"}`}></i>
      </button>

      {/* Overlay for mobile */}
      {isMobileOpen && (
        <div className="sidebar-overlay" onClick={toggleMobileSidebar}></div>
      )}

      <div className={`sidebar ${isMobileOpen ? "mobile-open" : ""}`}>
        <div className="sidebar-header">
          <img
            src="/logo.png"
            alt="Logo"
            className="sidebar-logo"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>

        {error && (
          <div className="sidebar-error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}

        {loading ? (
          <div className="sidebar-loading">
            <div className="skeleton-item"></div>
            <div className="skeleton-item"></div>
            <div className="skeleton-item"></div>
            <div className="skeleton-item"></div>
          </div>
        ) : (
          <ul className="sidebar-menu">
            {filteredMenuItems.map((item) => (
              <li key={item.path} className={isActive(item.path)}>
                <Link to={item.path} onClick={() => setIsMobileOpen(false)}>
                  <i className={item.icon}></i>
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="sidebar-footer">
          {userRole !== null && (
            <div className="user-role-badge">
              <i className="fas fa-user-circle"></i>
              <span>{userRole === 1 ? "Super Admin" : "Admin"}</span>
            </div>
          )}
          <button className="logout-button" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
